import { SchemeType } from '@/shared/types/api.types'
import type { CoverData, PlanValue } from './FlowSelectPlanWidget.types'
import HealthIcon from '@/shared/assets/images/health.png'
import BelongingsIcon from '@/shared/assets/images/belongings.png'
import TripIcon from '@/shared/assets/images/trip.png'

export const PROCEED_EVENT_NAME = 'proceed'
export const LOAD_EVENT_NAME = 'load'

export const EUROPE_SCHEME_CODE = '5004'
export const EUROPE_VARIANT = 'europe'
export const WORLDWIDE_VARIANT = 'worldwide'
export const SINGLE_VARIANT = 'single'

export const PLAN_DESCRIPTION_SCHEME_MAPPING: Record<string, Record<string, string[]>> = {
  [SchemeType.WorldwideExcl]: {
    // Case 1
    [`${SchemeType.Worldwide},${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.excl_all',
    ],
    // Case 2
    [`${SchemeType.FlyEurope},${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.visa',
      'outbound.select_plan.insurance_values.plan_desc.appl_world',
    ],
    // Case 3
    [`${SchemeType.Regional},${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.appl_world',
    ],
    // Case 4
    [`${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.appl_world',
    ],
  },
  [SchemeType.Worldwide]: {
    // Case 5
    [`${SchemeType.Worldwide},${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.excl',
    ],
    // Case 6
    [`${SchemeType.Worldwide}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.appl_world',
    ],
  },
  [SchemeType.Regional]: {
    // Case 7
    [`${SchemeType.Regional},${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.common',
      'outbound.select_plan.insurance_values.plan_desc.gcc',
    ],
  },
  [SchemeType.FlyEurope]: {
    // Case 8
    [`${SchemeType.FlyEurope},${SchemeType.WorldwideExcl}`]: [
      'outbound.select_plan.insurance_values.plan_desc.schengen',
    ],
  },
}

export const PLAN_DESCRIPTION_SCHEME_MAPPING_V3: Record<string, PlanValue[]> = {
  [SchemeType.Worldwide]: [
    { title: `outbound.select_plan.insurance_values.common_values` },
    { title: `outbound.select_plan.insurance_values.addons.winter_covid` },
    {
      title: `outbound.select_plan.insurance_values.addons.validity.all_countries`,
      tooltip: `outbound.select_plan.insurance_values.addons.validity.common_excl`,
    },
  ],
  [`${SchemeType.WorldwideExcl}:${SchemeType.Worldwide}`]: [
    { title: `outbound.select_plan.insurance_values.common_values` },
    { title: `outbound.select_plan.insurance_values.addons.winter_covid` },
    {
      title: `outbound.select_plan.insurance_values.addons.validity.excl_usa`,
      excluded: true,
      tooltip: `outbound.select_plan.insurance_values.addons.validity.also_common_excl`,
    },
  ],
  [`${SchemeType.WorldwideExcl}:${SchemeType.Regional}`]: [
    { title: `outbound.select_plan.insurance_values.common_values` },
    {
      title: `outbound.select_plan.insurance_values.addons.validity.valid_excl_usa`,
      tooltip: `outbound.select_plan.insurance_values.addons.validity.also_common_excl`,
    },
    {
      title: `outbound.select_plan.insurance_values.addons.covid`,
    },
  ],
  [`${SchemeType.WorldwideExcl}:${SchemeType.FlyEurope}`]: [
    { title: `outbound.select_plan.insurance_values.medical_care_ww` },
    {
      title: `outbound.select_plan.insurance_values.addons.validity.valid_excl_usa`,
      tooltip: `outbound.select_plan.insurance_values.addons.validity.also_common_excl`,
    },
    { title: `outbound.select_plan.insurance_values.belongings_incl` },
    { title: `outbound.select_plan.insurance_values.cancellation_incl` },
  ],
  [SchemeType.Regional]: [
    { title: `outbound.select_plan.insurance_values.common_values` },
    {
      title: `outbound.select_plan.insurance_values.addons.validity.regional`,
      tooltip: [
        `outbound.select_plan.insurance_values.addons.validity.regional_gcc`,
        `outbound.select_plan.insurance_values.addons.validity.regional_arab`,
        `outbound.select_plan.insurance_values.addons.validity.regional_isc`,
        `outbound.select_plan.insurance_values.addons.validity.regional_sea`,
      ],
    },
    {
      title: `outbound.select_plan.insurance_values.addons.not_covid`,
      excluded: true,
    },
  ],
  [SchemeType.FlyEurope]: [
    { title: `outbound.select_plan.insurance_values.medical_care_europe` },
    {
      title: `outbound.select_plan.insurance_values.addons.validity.europe`,
      tooltip: `outbound.select_plan.insurance_values.addons.validity.europe_countries`,
    },
    { title: `outbound.select_plan.insurance_values.belongings_excl`, excluded: true },
    { title: `outbound.select_plan.insurance_values.cancellation_excl`, excluded: true },
  ],
}

export const WORLDWIDE_EXCL_VARIANTS = [
  SchemeType.Worldwide,
  SchemeType.Regional,
  SchemeType.FlyEurope,
]

export const COVER_DATA: CoverData[] = [
  {
    title: 'outbound.select_plan.insurance_values.covered.health.header',
    data: [
      {
        text: 'outbound.select_plan.insurance_values.covered.health.em_medical_care.label',
        amount: 'outbound.select_plan.insurance_values.covered.health.em_medical_care.amount',
        tooltip: 'outbound.select_plan.insurance_values.covered.health.em_medical_care.tooltip',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.health.em_dental.label',
        amount: 'outbound.select_plan.insurance_values.covered.health.em_dental.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.health.rescue.label',
        amount: 'outbound.select_plan.insurance_values.covered.health.rescue.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.health.travel_accident.label',
        amount: 'outbound.select_plan.insurance_values.covered.health.travel_accident.amount',
      },
    ],
  },
  {
    title: 'outbound.select_plan.insurance_values.covered.trip.header',
    data: [
      {
        text: 'outbound.select_plan.insurance_values.covered.trip.cancellation.label',
        amount: 'outbound.select_plan.insurance_values.covered.trip.cancellation.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.trip.pers_liability.label',
        amount: 'outbound.select_plan.insurance_values.covered.trip.pers_liability.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.trip.legal_help.label',
        amount: 'outbound.select_plan.insurance_values.covered.trip.legal_help.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.trip.unsch_return.label',
        amount: 'outbound.select_plan.insurance_values.covered.trip.unsch_return.amount',
      },
    ],
  },
  {
    title: 'outbound.select_plan.insurance_values.covered.belongings.header',
    data: [
      {
        text: 'outbound.select_plan.insurance_values.covered.belongings.bag_loss.label',
        amount: 'outbound.select_plan.insurance_values.covered.belongings.bag_loss.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.belongings.passport.label',
        amount: 'outbound.select_plan.insurance_values.covered.belongings.passport.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.belongings.sports_gear.label',
        amount: 'outbound.select_plan.insurance_values.covered.belongings.sports_gear.amount',
      },
    ],
  },
  {
    title: 'outbound.select_plan.insurance_values.covered.addons.header',
    annotation: 'outbound.select_plan.insurance_values.covered.addons.annotation',
    data: [
      {
        text: 'outbound.select_plan.insurance_values.covered.addons.winter_sports.label',
        amount: 'outbound.select_plan.insurance_values.covered.addons.winter_sports.amount',
      },
      {
        text: 'outbound.select_plan.insurance_values.covered.addons.covid.label',
        amount: 'outbound.select_plan.insurance_values.covered.addons.covid.amount',
        annotation: 'outbound.select_plan.insurance_values.covered.addons.covid.worldwide_note',
      },
    ],
  },
]

export const PLAN_ANNOTATION_SCHEME_MAPPING: Record<string, string> = {
  [SchemeType.Worldwide]: 'outbound.select_plan.insurance_values.addons.validity.all_countries',
  [SchemeType.WorldwideExcl]: 'outbound.select_plan.insurance_values.addons.validity.excl_usa',
  [SchemeType.Regional]: 'outbound.select_plan.insurance_values.addons.validity.regional',
}

export const SINGLE_PLAN_COVERS = [
  {
    icon: HealthIcon,
    title: 'outbound.select_plan.insurance_values.covered.health.header',
    annotation: 'outbound.select_plan.insurance_values.covered.health.em_medical_care.desc',
    coverage: [
      {
        title: 'outbound.select_plan.insurance_values.covered.health.em_medical_care.label',
        price: 'outbound.select_plan.insurance_values.covered.health.em_medical_care.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.health.em_dental.label',
        price: 'outbound.select_plan.insurance_values.covered.health.em_dental.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.health.rescue.label',
        price: 'outbound.select_plan.insurance_values.covered.health.rescue.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.health.travel_accident.label',
        price: 'outbound.select_plan.insurance_values.covered.health.travel_accident.amount',
      },
    ],
  },
  {
    icon: TripIcon,
    title: 'outbound.select_plan.insurance_values.covered.trip.header',
    annotation: 'outbound.select_plan.insurance_values.covered.trip.desc',
    coverage: [
      {
        title: 'outbound.select_plan.insurance_values.covered.trip.cancellation.label',
        price: 'outbound.select_plan.insurance_values.covered.trip.cancellation.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.trip.pers_liability.label',
        price: 'outbound.select_plan.insurance_values.covered.trip.pers_liability.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.trip.legal_help.label',
        price: 'outbound.select_plan.insurance_values.covered.trip.legal_help.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.trip.unsch_return.label',
        price: 'outbound.select_plan.insurance_values.covered.trip.unsch_return.amount',
      },
    ],
  },
  {
    icon: BelongingsIcon,
    title: 'outbound.select_plan.insurance_values.covered.belongings.header',
    annotation: 'outbound.select_plan.insurance_values.covered.belongings.desc',
    coverage: [
      {
        title: 'outbound.select_plan.insurance_values.covered.belongings.bag_loss.label',
        price: 'outbound.select_plan.insurance_values.covered.belongings.bag_loss.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.belongings.passport.label',
        price: 'outbound.select_plan.insurance_values.covered.belongings.passport.amount',
      },
      {
        title: 'outbound.select_plan.insurance_values.covered.belongings.sports_gear.label',
        price: 'outbound.select_plan.insurance_values.covered.belongings.sports_gear.amount',
      },
    ],
  },
]
